import type { Dispatch } from 'redux';
import type { InitIdentity } from './useInitialization';
import { setSettings } from '../../actions/app.actions';
import { getUserSettings } from '../../actions/userSettings.actions';
import {
	getSubscriptionsUsageAction,
	getSubscriptionsUsageResolved,
} from '../../actions/subscriptionsUsage.actions';
import { updateWhatsNewState } from '../../actions/whatsNew.actions';
import { defaultCompanySettings } from '../../utils/companySettings';
import { getAuthenticationProvider } from '../../utils/getAuthenticationProvider';
import { populateEntitlementsToSubscriptions } from '../../utils/forgeLicense';
import { AuthType } from '../../types';
import type { getCurrentCompanySettings } from '../../utils/companySettings';

/**
 * A single unit of data-phase initialization work. Receives the resolved
 * identity and either runs synchronously (fire-and-forget dispatch → returns
 * `void`) or returns a Promise that gates data-phase completion.
 */
export type DataTask = (identity: InitIdentity) => Promise<void> | void;

/**
 * Compose data tasks into a single `runDataStep`. Tasks run concurrently with
 * per-task error isolation: one rejecting task can never abort the others or
 * fail initialization (best-effort, generalizing the prior company-settings
 * fallback). Resolves once every task has settled.
 */
export const composeDataTasks =
	(tasks: DataTask[]): ((identity: InitIdentity) => Promise<void>) =>
	async identity => {
		await Promise.allSettled(
			tasks.map(task => Promise.resolve(task(identity))),
		);
	};

export interface UserSettingsTaskDeps {
	dispatch: Dispatch;
}

/** Fire the user-settings fetch (fire-and-forget thunk dispatch). */
export const createUserSettingsTask =
	({ dispatch }: UserSettingsTaskDeps): DataTask =>
	() => {
		dispatch(getUserSettings());
	};

export interface SubscriptionsTaskDeps {
	dispatch: Dispatch;
}

/**
 * Fire the subscriptions-usage fetch. On the Forge auth path the entitlements
 * are already resolved on the identity, so we hydrate from those directly;
 * otherwise we dispatch the async usage fetch. Fire-and-forget either way.
 */
export const createSubscriptionsTask =
	({ dispatch }: SubscriptionsTaskDeps): DataTask =>
	identity => {
		if (getAuthenticationProvider() === AuthType.Forge) {
			const subscriptions = populateEntitlementsToSubscriptions(
				identity.entitlements,
			);
			dispatch(getSubscriptionsUsageResolved({ subscriptions }));
		} else {
			dispatch(getSubscriptionsUsageAction());
		}
	};

export interface CompanySettingsTaskDeps {
	dispatch: Dispatch;
	getCurrentApp: () => string;
	i18n: { changeLanguage: (lng: string) => void };
	getCurrentCompanySettings: typeof getCurrentCompanySettings;
}

/**
 * Load company settings, apply the resolved language, and seed whats-new state.
 * Owns its own error handling: on failure it falls back to default company
 * settings keyed to the org id so a settings failure cannot abort init. This
 * task is async and therefore gates data-phase completion.
 */
export const createCompanySettingsTask =
	({
		dispatch,
		getCurrentApp,
		i18n,
		getCurrentCompanySettings,
	}: CompanySettingsTaskDeps): DataTask =>
	async identity => {
		try {
			const { companySettings, lastWhatsNewVersionSeen } =
				await getCurrentCompanySettings(getCurrentApp());
			dispatch(setSettings({ companySettings }));
			i18n.changeLanguage(companySettings.language);
			dispatch(
				updateWhatsNewState({
					lastWhatsNewVersionSeen,
					isLoading: false,
				}) as never,
			);
		} catch {
			dispatch(
				setSettings({
					companySettings: {
						...defaultCompanySettings,
						organization: identity.defaultSite,
					},
				} as never),
			);
		}
	};
